import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import ReactDiffViewer from 'react-diff-viewer';

import { RemoteButton } from '../RemoteButton';
import { diffRevisionChunks, fetchRevision, fetchRouting, searchRevisionSummaries, setRoutingPointer } from './functionRevisionUtils';

// react-diff-viewer's split view leaves each side's content column auto-sized to its own
// longest line, so a short stub diffed against a long function produces lopsided columns.
// Each row is 6 tds: gutter/marker/content, twice (once per side). Pin the gutter/marker
// to their normal fixed pixel widths and split what's left 50/50 between the two content
// columns, so both sides end up exactly the same width.
const GUTTER_WIDTH = 40;
const MARKER_WIDTH = 25;
const diffViewerStyles = {
    diffContainer: {
        width: '100%',
        tableLayout: 'fixed',
        pre: {
            lineHeight: '18px',
        },
    },
    line: {
        '& td:nth-of-type(1), & td:nth-of-type(4)': {
            width: `${GUTTER_WIDTH}px`,
            minWidth: `${GUTTER_WIDTH}px`,
        },
        '& td:nth-of-type(2), & td:nth-of-type(5)': {
            width: `${MARKER_WIDTH}px`,
        },
        '& td:nth-of-type(3), & td:nth-of-type(6)': {
            width: `calc(50% - ${GUTTER_WIDTH + MARKER_WIDTH}px)`,
            wordBreak: 'break-word',
        },
    },
};

function revisionLabel(revision) {
    return `#${revision._id} - ${revision.description}`;
}

function ChangeStat({ added, removed }) {
    if (!added && !removed) {
        return null;
    }
    return (
        <span style={{ marginLeft: '8px', fontWeight: 'normal' }}>
            {added > 0 && <span style={{ color: '#22863a' }}>+{added}</span>}
            {added > 0 && removed > 0 && ' '}
            {removed > 0 && <span style={{ color: '#cb2431' }}>-{removed}</span>}
        </span>
    );
}

ChangeStat.propTypes = {
    added: PropTypes.number.isRequired,
    removed: PropTypes.number.isRequired,
};

function RevisionSelect({ label, revisions, value, onChange }) {
    return (
        <div>
            <label>{label}</label><br />
            <select value={value} onChange={e => onChange(e.target.value)}>
                <option value="">(empty)</option>
                {revisions.map(revision => (
                    <option key={revision._id} value={revision._id}>{revisionLabel(revision)}</option>
                ))}
            </select>
        </div>
    );
}

RevisionSelect.propTypes = {
    label: PropTypes.string.isRequired,
    revisions: PropTypes.array.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

function DiffSection({ section, collapsed, onToggle }) {
    return (
        <div style={{ marginBottom: '12px', border: '1px solid #ddd' }}>
            <div
                onClick={onToggle}
                style={{ cursor: 'pointer', userSelect: 'none', padding: '6px 10px', background: '#f5f5f5', fontFamily: 'monospace', fontSize: '16px' }}
            >
                {collapsed ? '▶' : '▼'} {section.name}
                <ChangeStat added={section.added} removed={section.removed} />
                {!section.changed && <span style={{ marginLeft: '8px', color: '#888', fontStyle: 'italic', fontWeight: 'normal' }}>(no changes)</span>}
            </div>
            {!collapsed &&
                <div style={{ padding: '10px' }}>
                    <ReactDiffViewer
                        oldValue={section.oldValue}
                        newValue={section.newValue}
                        splitView={true}
                        hideLineNumbers={false}
                        showDiffOnly={false}
                        styles={diffViewerStyles}
                    />
                </div>}
        </div>
    );
}

DiffSection.propTypes = {
    section: PropTypes.shape({
        name: PropTypes.string.isRequired,
        oldValue: PropTypes.string.isRequired,
        newValue: PropTypes.string.isRequired,
        changed: PropTypes.bool.isRequired,
        added: PropTypes.number.isRequired,
        removed: PropTypes.number.isRequired,
    }).isRequired,
    collapsed: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
};

const TARGET_LABELS = { dev: 'Dev', prod: 'Production' };

function TargetPanel({ target, selectedB, currentVersion, onConfirm, error, message }) {
    const label = TARGET_LABELS[target];
    return (
        <div style={{ margin: '12px 0', padding: '10px', border: '1px solid #ccc', background: '#fafafa' }}>
            <div>
                <b>{label}</b> currently points to {currentVersion != null ? `#${currentVersion}` : '(unset)'}.
            </div>
            {selectedB &&
                <RemoteButton name={`Update ${label} to #${selectedB}`} title={`Point ${target} at revision ${selectedB}`} onStart={onConfirm}>
                    Point <b>{target}</b> at revision {selectedB}?
                    {target === 'prod' && ' This takes effect for all live users almost immediately.'}
                </RemoteButton>}
            {!selectedB && <div>Select a <b>New (B)</b> revision above to set as {label}.</div>}
            {error && <div style={{ color: 'red' }}>{error}</div>}
            {message && <div style={{ color: '#22863a' }}>{message}</div>}
        </div>
    );
}

TargetPanel.propTypes = {
    target: PropTypes.oneOf(['dev', 'prod']).isRequired,
    selectedB: PropTypes.string.isRequired,
    currentVersion: PropTypes.number,
    onConfirm: PropTypes.func.isRequired,
    error: PropTypes.string,
    message: PropTypes.string,
};

function useRevisionContent(id) {
    const [content, setContent] = useState(null);
    useEffect(() => {
        if (!id) {
            setContent(null);
            return;
        }
        let cancelled = false;
        setContent(null);
        fetchRevision(id).then(result => {
            if (!cancelled) {
                setContent(result.error ? null : result.result);
            }
        });
        return () => { cancelled = true; };
    }, [id]);
    return content;
}

export function FunctionRevisionDiff() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [admin, setAdmin] = useState(null);
    const [routing, setRouting] = useState({});
    const [revisions, setRevisions] = useState([]);
    const [selectedA, setSelectedA] = useState(() => new URLSearchParams(window.location.search).get('a') || '');
    const [selectedB, setSelectedB] = useState(() => new URLSearchParams(window.location.search).get('b') || '');
    const [target] = useState(() => {
        const value = new URLSearchParams(window.location.search).get('target');
        return value === 'dev' || value === 'prod' ? value : null;
    });
    const [targetError, setTargetError] = useState(null);
    const [targetMessage, setTargetMessage] = useState(null);

    const contentA = useRevisionContent(selectedA);
    const contentB = useRevisionContent(selectedB);
    const sections = useMemo(() => diffRevisionChunks(contentA, contentB), [contentA, contentB]);
    const totals = useMemo(() => sections.reduce((acc, section) => ({
        filesChanged: acc.filesChanged + (section.changed ? 1 : 0),
        added: acc.added + section.added,
        removed: acc.removed + section.removed,
    }), { filesChanged: 0, added: 0, removed: 0 }), [sections]);

    const [collapsed, setCollapsed] = useState(() => new Set());

    // Reset collapse state whenever the compared revisions change - unchanged files start
    // collapsed again, changed ones start expanded.
    useEffect(() => {
        setCollapsed(new Set(sections.filter(section => !section.changed).map(section => section.name)));
    }, [sections]);

    const toggleSection = name => {
        setCollapsed(previous => {
            const next = new Set(previous);
            if (next.has(name)) {
                next.delete(name);
            } else {
                next.add(name);
            }
            return next;
        });
    };

    useEffect(() => {
        async function run() {
            const [meResult, routingResult, revisionsResult] = await Promise.all([
                fetch('/api/me').then(r => r.json()),
                fetchRouting(),
                searchRevisionSummaries(),
            ]);
            if (routingResult.error || revisionsResult.error) {
                setError(routingResult.error || revisionsResult.error);
                setLoading(false);
                return;
            }
            setAdmin(meResult.user);
            setRouting(routingResult.result);
            setRevisions(revisionsResult.results);
            setLoading(false);
        }
        run();
    }, []);

    // Keep the selection shareable/bookmarkable via the url.
    useEffect(() => {
        const params = new URLSearchParams();
        if (selectedA) {
            params.set('a', selectedA);
        }
        if (selectedB) {
            params.set('b', selectedB);
        }
        if (target) {
            params.set('target', target);
        }
        const query = params.toString();
        window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    }, [selectedA, selectedB, target]);

    const confirmSetTarget = async () => {
        setTargetError(null);
        setTargetMessage(null);
        const result = await setRoutingPointer(target, Number(selectedB), admin, !!routing[target]);
        if (result.error) {
            setTargetError(result.error);
            return;
        }
        const routingResult = await fetchRouting();
        if (routingResult.error) {
            setTargetError(routingResult.error);
            return;
        }
        setRouting(routingResult.result);
        setTargetMessage(`${TARGET_LABELS[target]} now points to #${selectedB}.`);
    };

    return (
        <div style={{ padding: '18px' }}>
            <h1>Diff Lua Revisions</h1>
            <div><a href="/lua">&larr; Back to revisions</a></div>
            {error && <div style={{ color: 'red' }}>{error}</div>}
            {loading && <div>Loading...</div>}
            {!loading &&
                <div style={{ display: 'flex', gap: '24px', margin: '12px 0' }}>
                    <RevisionSelect label="Old (A)" revisions={revisions} value={selectedA} onChange={setSelectedA} />
                    <RevisionSelect label="New (B)" revisions={revisions} value={selectedB} onChange={setSelectedB} />
                </div>}
            {!loading && target &&
                <TargetPanel
                    target={target}
                    selectedB={selectedB}
                    currentVersion={routing[target] && routing[target].version}
                    onConfirm={confirmSetTarget}
                    error={targetError}
                    message={targetMessage}
                />}
            {!loading && !selectedA && !selectedB && <div>Select a revision above to see its content or diff it against another.</div>}
            {!loading && (selectedA || selectedB) && sections.length === 0 && <div>No functions/jobs/mods found.</div>}
            {!loading && sections.length > 0 &&
                <div style={{ margin: '12px 0' }}>
                    {totals.filesChanged} of {sections.length} file(s) changed
                    <ChangeStat added={totals.added} removed={totals.removed} />
                </div>}
            {sections.map(section => (
                <DiffSection
                    key={section.name}
                    section={section}
                    collapsed={collapsed.has(section.name)}
                    onToggle={() => toggleSection(section.name)}
                />
            ))}
        </div>
    );
}
